import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import _, { isArray } from "lodash";
import { $Button, $Modal, $Input, Pagination } from "us.common/components";
import { SearchOutlined } from "us.icons";
import * as Actions from "actions";
import "./preview.scss";

import { getPageProperties, getTemplateAdapterIds } from "functions";
import { useTranslation } from "react-i18next";
import { RootState } from "us.helper/types";
import { DocumentType } from "constants/TemplateEditor";
import { Formik } from "formik";
import { useLocation } from "react-router-dom";
import { GetTemplatePreview } from "./Repository/GetTemplatePreview";
import { BasicPreview, LivePreview } from "./components";

type PreviewProps = {
  defaultOptions: any;
  onClose: () => void;
};

/**
 * @description - Preview for the current document
 * @link Design Document -
 * @author Roshan Maddumage <roshanma@unicorn-solutions.com>
 * @since 11/23/2022
 */
const Preview: React.FC<PreviewProps & PropsFromRedux> = (props) => {
  const {
    template,
    defaultOptions,
    previewData,
    templatePreview,
    resetPreviewData,
    onClose,
    getTemplatePreview,
    resetTemplatePreview,
  } = props;
  const { t } = useTranslation();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const isPreviewVisible = urlParams.get("preview");
  const [isLivePreview, setlivePreview] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    if (
      !templatePreview.isLoading &&
      isArray(templatePreview.data) &&
      templatePreview.data.length > 0
    ) {
      setlivePreview(true);
    } else {
      setlivePreview(false);
    }
  }, [templatePreview]);

  const pageProperties = useMemo(() => {
    return getPageProperties(template.data?.outPutFormat as DocumentType);
  }, [template.data?.outPutFormat]);

  const handleSubmit = (values: any) => {
    if (values.caseNo) {
      const adapterIds = getTemplateAdapterIds({
        template: template.data,
        isRepeating: false,
        all: true,
      });

      const payload = GetTemplatePreview.call({
        templateId: template.data.templateId,
        adapterIds: adapterIds,
        caseNo: values.caseNo,
      });
      getTemplatePreview && getTemplatePreview(payload);
    }
  };

  /**
   * Page Navigation
   */
  const onNavigation = (toNext: boolean) => {
    toNext ? setCurrentPage(currentPage + 1) : setCurrentPage(currentPage - 1);
  };

  return (
    <Formik
      initialValues={{
        caseNo: "",
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
    >
      {({ handleSubmit, resetForm }: any) => (
        <$Modal
          open={Boolean(isPreviewVisible)}
          title={
            <div className="d-flex flex-fill justify-content-between input-number-hide-arrows mr-4">
              <span>
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_PREVIEW")}
              </span>
              <div>
                {previewData.data.length > 0 && (
                  <$Button
                    type="link"
                    size="small"
                    onClick={() => resetPreviewData && resetPreviewData({})}
                  >
                    {t("COMMON.RESET")}
                  </$Button>
                )}
                <$Input
                  size="small"
                  type="number"
                  className="mr-3"
                  style={{ width: "200px" }}
                  name="caseNo"
                  placeholder={t("COMMON.CASE_NO")}
                  onPressEnter={handleSubmit}
                  suffix={
                    <$Button
                      type="link"
                      size="small"
                      icon={<SearchOutlined />}
                      loading={previewData.isLoading}
                      onClick={handleSubmit}
                    />
                  }
                />
              </div>
            </div>
          }
          width={pageProperties.widthPx ? pageProperties.widthPx + 100 : 900}
          onCancel={() => {
            resetPreviewData && resetPreviewData({});
            resetTemplatePreview && resetTemplatePreview({});
            resetForm();
            onClose();
          }}
          okButtonProps={{ style: { display: "none" } }}
          footer={
            <Pagination
              dataLength={template.data?.templateContent?.pages?.length ?? 1}
              currentPage={currentPage}
              onNavigation={onNavigation}
            />
          }
          className="preview-page-modal"
        >
          {isLivePreview ? (
            <LivePreview pageIndex={currentPage} />
          ) : (
            <BasicPreview
              pageIndex={currentPage}
              defaultOptions={defaultOptions}
            />
          )}
        </$Modal>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { section, parameters, adapters, previewData, templatePreview } =
    templateEditor;
  const { template } = templatesHistory;

  return {
    section,
    parameters,
    template,
    adapters,
    previewData,
    templatePreview,
  };
};

const { previewData, templatePreview } = Actions.templateEditor;

const mapDispatchToProps = {
  resetPreviewData: previewData.reset,
  getPreviewData: previewData.get,
  getTemplatePreview: templatePreview.get,
  resetTemplatePreview: templatePreview.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Preview);
